import { useEffect, useState } from 'react';
import axios from 'axios';
import { Play, X, ChevronLeft, ChevronRight, Film, Loader2 } from 'lucide-react';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${API}/videos`);
        setVideos(response.data);
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const openVideoPlayer = (video, index) => {
    setSelectedVideo(video);
    setCurrentIndex(index);
  };

  const closeVideoPlayer = () => {
    setSelectedVideo(null);
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    const newIndex = currentIndex === 0 ? videos.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedVideo(videos[newIndex]);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    const newIndex = currentIndex === videos.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedVideo(videos[newIndex]);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedVideo) return;
      if (e.key === 'Escape') closeVideoPlayer();
      if (e.key === 'ArrowLeft') goToPrevious(e);
      if (e.key === 'ArrowRight') goToNext(e);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedVideo, currentIndex]);

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <main className="py-12 lg:py-20 min-h-screen bg-[#FAF8F5]">
        <div className="container mx-auto px-4 flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-[#E85D75]" />
        </div>
      </main>
    );
  }

  return (
    <main className="py-12 lg:py-20 min-h-screen bg-[#FAF8F5]">
      <SEO 
        title="Kitten Videos | KittenState Scottish Fold & Scottish Straight"
        description="Watch adorable videos of our Scottish Fold and Scottish Straight kittens at KittenState. See our playful, healthy kittens in action before scheduling a visit."
        keywords="Scottish Fold kitten videos, Scottish Straight videos, KittenState videos, cute kitten videos"
        url="https://kittenstate.com/videos"
      />
      
      <div className="container mx-auto px-4 md:px-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-10 lg:mb-16">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E85D75] mb-3">
            Watch & Enjoy
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold tracking-tight leading-tight text-stone-800 mb-4 lg:mb-6">
            Kitten Videos
          </h1>
          <p className="text-base lg:text-lg leading-relaxed text-stone-600 max-w-2xl mx-auto">
            Watch our adorable kittens in action — playing, exploring, and showing off their personalities.
          </p>
        </div>

        {/* Video Grid */}
        {videos.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center shadow-lg shadow-stone-200/50">
            <Film className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-serif font-medium text-stone-800 mb-2">
              Videos Coming Soon
            </h3>
            <p className="text-stone-500">
              We're preparing some adorable kitten videos for you. Check back soon!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {videos.map((video, index) => (
              <div
                key={video.id}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg shadow-stone-200/50 hover:shadow-xl hover:shadow-[#E85D75]/10 transition-all duration-300 cursor-pointer"
                onClick={() => openVideoPlayer(video, index)}
                data-testid={`video-tile-${video.id}`}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-stone-100 overflow-hidden">
                  {video.thumbnail_url ? (
                    <img
                      src={video.thumbnail_url}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-100 to-stone-200">
                      <Film className="w-16 h-16 text-stone-300" />
                    </div>
                  )}
                  
                  {/* Play button overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-[#E85D75] flex items-center justify-center shadow-lg shadow-[#E85D75]/30 group-hover:scale-110 transition-transform duration-300">
                      <Play className="w-7 h-7 text-white ml-1" fill="white" />
                    </div>
                  </div>

                  {/* Duration badge */}
                  {video.duration && (
                    <div className="absolute bottom-3 right-3 bg-black/70 text-white text-sm px-2.5 py-1 rounded-lg font-medium">
                      {formatDuration(video.duration)}
                    </div>
                  )}

                  {/* Hover gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                {/* Info */}
                {video.title && (
                  <div className="p-4">
                    <h3 className="font-medium text-stone-800 group-hover:text-[#E85D75] transition-colors line-clamp-1">
                      {video.title}
                    </h3>
                    {video.description && (
                      <p className="text-sm text-stone-500 mt-1 line-clamp-2">
                        {video.description}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        {videos.length > 0 && (
          <div className="mt-12 lg:mt-16 bg-white rounded-3xl p-6 lg:p-10 text-center shadow-lg shadow-stone-200/50 border border-rose-100/50">
            <h3 className="text-2xl lg:text-3xl font-serif font-medium text-stone-800 mb-3">
              Ready to Meet Our Kittens?
            </h3>
            <p className="text-stone-600 mb-6 max-w-lg mx-auto">
              Schedule a video call to meet our adorable kittens in person and find your perfect companion.
            </p>
            <a
              href="/kittens"
              className="inline-flex items-center gap-2 bg-[#E85D75] text-white hover:bg-[#D64560] px-8 py-4 rounded-full font-medium shadow-lg shadow-[#E85D75]/25 transition-all hover:scale-105 active:scale-95"
            >
              <Play className="w-4 h-4" />
              View Available Kittens
            </a>
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={closeVideoPlayer}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-10"
            onClick={closeVideoPlayer}
            data-testid="close-video-player"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Navigation arrows */}
          {videos.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"
                onClick={goToPrevious}
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"
                onClick={goToNext}
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          {/* Video container */}
          <div 
            className="max-w-5xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              key={selectedVideo.id}
              src={selectedVideo.video_url}
              className="w-full max-h-[75vh] rounded-2xl bg-black"
              controls
              autoPlay
              playsInline
              muted={false}
              data-testid="video-player"
            />
            
            {/* Video info */}
            <div className="mt-4 text-center">
              {selectedVideo.title && (
                <h3 className="text-white text-xl font-serif">{selectedVideo.title}</h3>
              )}
              {selectedVideo.description && (
                <p className="text-white/70 text-sm mt-2 max-w-2xl mx-auto">
                  {selectedVideo.description}
                </p>
              )}
              {videos.length > 1 && (
                <p className="text-white/50 text-sm mt-3">
                  {currentIndex + 1} / {videos.length}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
